import OrderFinishedListingFilter, {
  OrderFinishedListingCargoCompanyExternalOrderIdFilter,
  OrderFinishedListingCargoCompanyInternalOrderIdFilter,
  OrderFinishedListingClientFilter,
  OrderFinishedListingDispatchFilter,
  OrderFinishedListingDriverFilter,
  OrderFinishedListingPassengerFilter,
  OrderFinishedListingPublicStatusFilter,
  OrderFinishedListingRouteAddressFilter,
  OrderFinishedListingRouteDestinationAddressFilter,
  OrderFinishedListingRouteIntermediateAddressFilter,
  OrderFinishedListingRoutePickupAddressFilter,
  OrderFinishedListingExecutionStatusFilter,
  OrderFinishedListingTaxiFilter,
  OrderFinishedListingUrgentOnlyFilter,
  OrderFinishedListingOrderStartDateFilter,
} from "./types/order-finished-listing-filter";
import OrderFinishedListingFilterType from "./types/order-finished-listing-filter-type";
import OrderFinishedListingPublicStatus from "./types/order-finished-listing-public-status";
import OrderFinishedListingSortKey from "./types/order-finished-listing-sort-key";
import OrderFinishedListingExecutionStatus from "./types/order-finished-listing-execution-status";
import OrderCargoOrderFinishedDownloadParams, {
  OrderCargoOrderFinishedDownloadParamsSortKey,
} from "../../../../common/services/order/cargo-order/finished/download/order-cargo-order-finished-download-params";
import PublicStatus from "../../../../common/types/public-status";
import ExecutionStatus from "../../../../common/types/execution-status";

const createOrder = (
  sortKey: OrderFinishedListingSortKey | null,
): OrderCargoOrderFinishedDownloadParamsSortKey | undefined => {
  const definition: Record<
    OrderFinishedListingSortKey,
    OrderCargoOrderFinishedDownloadParamsSortKey
  > = {
    [OrderFinishedListingSortKey.CARGO_COMPANY_INTERNAL_ID_ASC]:
      OrderCargoOrderFinishedDownloadParamsSortKey.INTERNAL_ORDER_ID_ASC,
    [OrderFinishedListingSortKey.CARGO_COMPANY_INTERNAL_ID_DESC]:
      OrderCargoOrderFinishedDownloadParamsSortKey.INTERNAL_ORDER_ID_DESC,
    [OrderFinishedListingSortKey.START_TIME_ASC]:
      OrderCargoOrderFinishedDownloadParamsSortKey.ORDER_START_DATE_ASC,
    [OrderFinishedListingSortKey.START_TIME_DESC]:
      OrderCargoOrderFinishedDownloadParamsSortKey.ORDER_START_DATE_DESC,
  };

  return sortKey ? definition[sortKey] : undefined;
};

const createPublicStatus = (
  status: OrderFinishedListingPublicStatus | undefined,
): PublicStatus | undefined => {
  switch (status) {
    case OrderFinishedListingPublicStatus.PRIVATE:
      return PublicStatus.PRIVATE;
    case OrderFinishedListingPublicStatus.PUBLISHED:
      return PublicStatus.PUBLISHED;
    case OrderFinishedListingPublicStatus.PUBLIC:
      return PublicStatus.PUBLIC;
    default:
      return undefined;
  }
};

const createExecutionStatus = (
  status: OrderFinishedListingExecutionStatus | undefined,
): ExecutionStatus | undefined => {
  switch (status) {
    case OrderFinishedListingExecutionStatus.CANCELLED:
      return ExecutionStatus.CANCELLED;
    case OrderFinishedListingExecutionStatus.FINISHED:
      return ExecutionStatus.FINISHED;

    default:
      return undefined;
  }
};

const create = (
  filters: OrderFinishedListingFilter[],
  sortKey: OrderFinishedListingSortKey | null,
): OrderCargoOrderFinishedDownloadParams => {
  const publicStatusFilter = filters.find(
    (filter) => filter.type === OrderFinishedListingFilterType.PUBLIC_STATUS,
  )?.value as OrderFinishedListingPublicStatusFilter["value"] | undefined;

  const executionStatusFilter = filters.find(
    (filter) => filter.type === OrderFinishedListingFilterType.EXECUTION_STATUS,
  )?.value as OrderFinishedListingExecutionStatusFilter["value"] | undefined;

  const urgentOnlyFilter = filters.find(
    (filter) => filter.type === OrderFinishedListingFilterType.URGENT_ONLY,
  )?.value as OrderFinishedListingUrgentOnlyFilter["value"] | undefined;

  const orderStartDateFilterValue = filters.find(
    (filter) => filter.type === OrderFinishedListingFilterType.ORDER_START_DATE,
  )?.value as OrderFinishedListingOrderStartDateFilter["value"] | undefined;

  return {
    sortKey: createOrder(sortKey),
    client: filters.find(
      (filter) => filter.type === OrderFinishedListingFilterType.CLIENT,
    )?.value as OrderFinishedListingClientFilter["value"] | undefined,
    internalOrderId: filters.find(
      (filter) =>
        filter.type ===
        OrderFinishedListingFilterType.CARGO_COMPANY_INTERNAL_ORDER_ID,
    )?.value as
      | OrderFinishedListingCargoCompanyInternalOrderIdFilter["value"]
      | undefined,
    externalOrderId: filters.find(
      (filter) =>
        filter.type ===
        OrderFinishedListingFilterType.CARGO_COMPANY_EXTERNAL_ORDER_ID,
    )?.value as
      | OrderFinishedListingCargoCompanyExternalOrderIdFilter["value"]
      | undefined,
    taxi: filters.find(
      (filter) => filter.type === OrderFinishedListingFilterType.TAXI,
    )?.value as OrderFinishedListingTaxiFilter["value"] | undefined,
    dispatch: filters.find(
      (filter) => filter.type === OrderFinishedListingFilterType.DISPATCH,
    )?.value as OrderFinishedListingDispatchFilter["value"] | undefined,
    pickupAddress: filters.find(
      (filter) =>
        filter.type === OrderFinishedListingFilterType.ROUTE_PICKUP_ADDRESS,
    )?.value as
      | OrderFinishedListingRoutePickupAddressFilter["value"]
      | undefined,
    intermediateAddress: filters.find(
      (filter) =>
        filter.type ===
        OrderFinishedListingFilterType.ROUTE_INTERMEDIATE_ADDRESS,
    )?.value as
      | OrderFinishedListingRouteIntermediateAddressFilter["value"]
      | undefined,
    destinationAddress: filters.find(
      (filter) =>
        filter.type ===
        OrderFinishedListingFilterType.ROUTE_DESTINATION_ADDRESS,
    )?.value as
      | OrderFinishedListingRouteDestinationAddressFilter["value"]
      | undefined,
    address: filters.find(
      (filter) => filter.type === OrderFinishedListingFilterType.ROUTE_ADDRESS,
    )?.value as OrderFinishedListingRouteAddressFilter["value"] | undefined,
    passenger: filters.find(
      (filter) => filter.type === OrderFinishedListingFilterType.PASSENGER,
    )?.value as OrderFinishedListingPassengerFilter["value"] | undefined,
    driver: filters.find(
      (filter) => filter.type === OrderFinishedListingFilterType.DRIVER,
    )?.value as OrderFinishedListingDriverFilter["value"] | undefined,
    publicStatus: createPublicStatus(publicStatusFilter),
    executionStatus: createExecutionStatus(executionStatusFilter),
    isUrgent: urgentOnlyFilter,
    orderStartDateFrom: orderStartDateFilterValue?.from ?? undefined,
    orderStartDateTo: orderStartDateFilterValue?.to ?? undefined,
  };
};

const orderFinishedListingReportLoadParamsFactory = {
  create,
};

export default orderFinishedListingReportLoadParamsFactory;
